// pages/TerminalFinder.jsx
import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import ReviewsBar from "../components/ReviewsBar";
import api from "../utils/api";
import "../global.css";
import "../styles/faqPage.css"; // reuse same clean layout

export default function TerminalFinder() {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  // Auto-run if ?airline=Aer Lingus etc.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const name = params.get("airline");

    if (name) {
      setQuery(name);
      autoSearch(name);
    }
  }, []);

  const autoSearch = async (airline) => {
    setLoading(true);
    try {
      const res = await api.get(
        `/api/airlines/routes?airline=${encodeURIComponent(airline)}`
      );
      setResults(res.data.routes || []);
    } catch {
      setResults([]);
    }
    setLoading(false);
    setSearched(true);
  };

  const search = async () => {
    setSearched(true);

    if (query.trim().length < 3) {
      setResults([]);
      return;
    }

    setLoading(true);

    try {
      const res = await api.get(
        `/api/airlines/routes?airline=${encodeURIComponent(query)}`
      );
      setResults(Array.isArray(res.data.routes) ? res.data.routes : []);
    } catch {
      setResults([]);
    }

    setLoading(false);
  };

  return (
    <div className="faq-page">
      {/* ========================================================
         ⭐ SEO BLOCK
      ========================================================= */}
      <Helmet>
        <title>
          Heathrow Terminal Finder – Airline Terminal Lookup | CheapCabGrab
        </title>

        <meta
          name="description"
          content="Find which Heathrow terminal your airline uses. Search any airline in seconds. Includes links to taxi, minibus and airport transfer services."
        />

        <link rel="canonical" href="https://www.cheapcabgrab.com/terminalFinder" />

        <meta
          name="keywords"
          content="Heathrow terminal finder, which terminal, airline terminal, Heathrow Terminal 2, Heathrow Terminal 3, Heathrow Terminal 5, airline lookup"
        />

        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Heathrow Terminal Finder – Airline Terminal Lookup" />
        <meta
          property="og:description"
          content="Search any airline to find the correct Heathrow terminal. Includes booking links for taxis and airport transfers."
        />
        <meta property="og:url" content="https://www.cheapcabgrab.com/terminalFinder" />

        {/* JSON-LD: Search page with SearchAction */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            name: "Heathrow Terminal Finder",
            description:
              "Search airlines to find the correct Heathrow Terminal (T2, T3, T4, T5). Updated automatically.",
            potentialAction: {
              "@type": "SearchAction",
              target:
                "https://www.cheapcabgrab.com/terminalFinder?airline={search_term_string}",
              "query-input": "required name=search_term_string",
            },
          })}
        </script>
      </Helmet>

      {/* ========================================================
         ⭐ PAGE GRID
      ========================================================= */}
      <div className="faq-grid">
        {/* ---------------- LEFT CONTENT ---------------- */}
        <div className="faq-left">
          <header className="faq-header">
            <h1>✈️ Heathrow Terminal Finder</h1>
            <p>
              Search any airline to instantly find the correct Heathrow Terminal
              (T2, T3, T4 or T5).  
              <br />
              Taxi & transfer links included for fast airport travel.
            </p>
          </header>

          {/* Search input */}
          <div style={{ marginBottom: "20px" }}>
            <input
              type="text"
              className="finder-modern-input"
              placeholder="Search airline (min 3 letters)…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />

            <button className="finder-modern-btn" onClick={search}>
              Search
            </button>
          </div>

          {/* Validation */}
          {searched && query.length < 3 && (
            <p style={{ color: "#b91c1c", fontWeight: 600 }}>
              Please enter at least 3 characters.
            </p>
          )}

          {loading && <p>Loading…</p>}

          {/* Results */}
          {searched && query.length >= 3 && !loading && (
            <>
              {Array.isArray(results) && results.length > 0 ? (
                results.map((r, idx) => (
                  <div className="terminal-card-clean" key={idx}>
                    <h3>{r.airline}</h3>
                    <p><strong>Airport:</strong> {r.airport}</p>
                    <p><strong>Terminal:</strong> {r.terminal}</p>
                    {r.city && <p><strong>City:</strong> {r.city}</p>}

                    <div className="terminal-clean-actions">
                      <Link
                        className="terminal-book-btn-clean"
                        to="/heathrow-taxi"
                      >
                        🚖 Taxi to Terminal {r.terminal}
                      </Link>

                      <Link
                        className="terminal-contact-btn-clean"
                        to={`/airlineContacts?airline=${encodeURIComponent(
                          r.airline
                        )}`}
                      >
                        📞 Airline Contact
                      </Link>
                    </div>
                  </div>
                ))
              ) : (
                <p>No results found.</p>
              )}
            </>
          )}

          {/* Helpful internal links */}
          <section className="faq-section" style={{ marginTop: "40px" }}>
            <h2>🚖 Useful Pages</h2>
            <ul>
              <li><Link className="faq-link" to="/heathrow-taxi">Heathrow Taxi Transfers</Link></li>
              <li><Link className="faq-link" to="/airlineContacts">Airline Contact Directory</Link></li>
              <li><Link className="faq-link" to="/heathrow-attractions">Heathrow Attractions</Link></li>
              <li><Link className="faq-link" to="/heathrow-hotels">Hotels Near Heathrow</Link></li>
            </ul>
          </section>

          <div style={{ marginTop: "40px" }}>
            <Link to="/" className="event-footer-cta">
              🚖 Get an Instant Taxi Quote
            </Link>
          </div>
        </div>

        {/* ---------------- RIGHT REVIEWS ---------------- */}
        <aside className="faq-right">
          <div className="faq-reviews-card">
            <h2>⭐ Customer Reviews</h2>
            <ReviewsBar />
          </div>
        </aside>
      </div>
    </div>
  );
}

